import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import { INK, FG_2, PURPLE, PRIMARY_TINT, LINE, FONTS } from '../sections/theme.js';

const CAT_KEYS = ['Graduation', 'Completion', 'First-aid', 'Participation'];

const TPL = {
    Graduation: [1, 2, 3, 4, 5].map((n) => ({ layout: 'img', img: `/images/home/tpl-grad-${n}.jpg` })),
    Completion: [1, 2, 3, 4, 5].map((n) => ({ layout: 'img', img: `/images/home/tpl-compl-${n}.jpg` })),
    'First-aid': [
        {
            layout: 'classic',
            bg: '#f5f9ff',
            accent: '#2f6db5',
            frame: '1.5px solid #cdddf2',
            title: 'FIRST AID / CPR CERTIFICATE',
            sub: 'Presented to',
            name: 'Oliver Grant',
            body: 'Introduction to First Aid & Emergency Response',
            s1n: 'L. Foster',
            s1r: 'Nurse',
            s2n: 'J. Reed',
            s2r: 'Instructor',
        },
        {
            layout: 'band',
            accent: '#c0392b',
            title: 'First Aid Training Certificate',
            sub: 'This certificate is awarded to',
            name: 'Harper Quinn',
            body: 'Qualified to administer emergency first aid',
            cid: 'FA-238174',
            s1n: 'L. Brooks',
            s1r: 'Responder',
            s2n: 'N. Davis',
            s2r: 'Specialist',
        },
        {
            layout: 'classic',
            bg: '#fbfcff',
            accent: '#1f3a8a',
            frame: '2px double #1f3a8a',
            title: 'EMERGENCY FIRST AID TRAINING',
            sub: 'Proudly presented to',
            name: 'Daniel Cross',
            body: 'Basic First Aid Mastery Training',
            s1n: 'E. Carter',
            s1r: 'Coordinator',
            s2n: 'A. Roberts',
            s2r: 'Nurse',
        },
        {
            layout: 'band',
            accent: '#16306b',
            title: 'First Aid Training Certificate',
            sub: 'This certificate is awarded to',
            name: 'Abigail Stone',
            body: 'First Aid Fundamentals — immediate assistance',
            cid: 'FA-340272',
            s1n: 'L. Bennett',
            s1r: 'Responder',
            s2n: 'E. Thompson',
            s2r: 'Nurse',
        },
        {
            layout: 'ornate',
            bg: '#f4f8ff',
            accent: '#2A6FDB',
            title: 'ADVANCED FIRST AID',
            sub: 'this is proudly presented to',
            name: 'Jackson Ford',
            body: 'advanced and life-sustaining assistance',
            cid: 'FA-232481',
            s1n: 'S. Harris',
        },
    ],
    Participation: [
        {
            layout: 'classic',
            bg: '#fbffff',
            accent: '#2f9bb8',
            frame: '1.5px solid #2f9bb8',
            title: 'CERTIFICATE OF PARTICIPATION',
            sub: 'Awarded to',
            name: 'Lucas Reed',
            body: 'for active participation in the 2026 cohort',
            s1n: 'M. Lee',
            s1r: 'Coordinator',
            s2n: 'A. Frost',
            s2r: 'Director',
        },
        {
            layout: 'ornate',
            bg: '#fbf2f4',
            accent: '#a8607a',
            title: 'OF PARTICIPATION',
            sub: 'this certificate is proudly presented to',
            name: 'Isabella Moore',
            body: 'for participation in the annual symposium',
            cid: 'PT-30210',
            s1n: 'C. Hawkins',
        },
        {
            layout: 'band',
            accent: '#5124A6',
            title: 'Participation Certificate',
            sub: 'Presented to',
            name: 'Ethan Blake',
            body: 'Innovation Workshop 2026',
            cid: 'PT-77120',
            s1n: 'R. Cole',
            s1r: 'Host',
            s2n: 'J. Pace',
            s2r: 'Director',
        },
        {
            layout: 'classic',
            bg: '#f5faf6',
            accent: '#1F8A5B',
            frame: '1.5px solid #1F8A5B',
            title: 'CERTIFICATE OF PARTICIPATION',
            sub: 'This certifies that',
            name: 'Chloe Adams',
            body: 'for taking part in the community program',
            s1n: 'S. Diaz',
            s1r: 'Coordinator',
            s2n: 'P. Lang',
            s2r: 'Director',
        },
        {
            layout: 'band',
            accent: '#2A6FDB',
            title: 'Event Participation',
            sub: 'Awarded to',
            name: 'Mason Lee',
            body: 'Global Education Forum 2026',
            cid: 'PT-44218',
            s1n: 'A. Webb',
            s1r: 'Organizer',
            s2n: 'R. Beck',
            s2r: 'Chair',
        },
    ],
};

const cardSx = {
    aspectRatio: '3 / 4',
    border: `1px solid ${LINE}`,
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 12px 30px -20px rgba(20,4,77,.28)',
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 22px 46px -22px rgba(20,11,61,.28)' },
};

const Seal = ({ color, light }) => (
    <Box
        sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            flex: 'none',
            background: light ? '#fff' : color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: light ? color : '#fff' }} />
    </Box>
);

const Signatures = ({ tp }) => (
    <Stack
        direction="row"
        spacing={2}
        sx={{
            justifyContent: 'center',
            width: '88%',
            mt: 0.5,
        }}
    >
        {[
            [tp.s1n, tp.s1r],
            [tp.s2n, tp.s2r],
        ].map(([n, r], i) =>
            n ? (
                <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                    <Box
                        sx={{
                            fontFamily: FONTS.serif,
                            fontStyle: 'italic',
                            fontSize: '7px',
                            color: '#6a6470',
                            lineHeight: 1,
                        }}
                    >
                        {n}
                    </Box>
                    <Box sx={{ height: '1px', background: '#cfcabd', my: '1px' }} />
                    {r && <Box sx={{ fontSize: '4px', color: '#a39bb0' }}>{r}</Box>}
                </Box>
            ) : null,
        )}
    </Stack>
);

function ClassicCert({ tp }) {
    return (
        <Box sx={{ width: '100%', height: '100%', p: '9px', background: tp.bg }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    border: tp.frame,
                    borderRadius: '3px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    p: '13px 11px',
                    textAlign: 'center',
                }}
            >
                <Seal color={tp.accent} />
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontWeight: 700,
                        fontSize: '9.5px',
                        color: tp.accent,
                        lineHeight: 1.25,
                    }}
                >
                    {tp.title}
                </Box>
                <Box sx={{ fontFamily: FONTS.serif, fontStyle: 'italic', fontSize: '6px', color: '#a39bb0' }}>
                    {tp.sub}
                </Box>
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#2a2730',
                        lineHeight: 1.1,
                    }}
                >
                    {tp.name}
                </Box>
                <Box sx={{ width: '50%', height: '1px', background: tp.accent, opacity: 0.4 }} />
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontSize: '6px',
                        color: '#a39bb0',
                        lineHeight: 1.4,
                        maxWidth: '92%',
                    }}
                >
                    {tp.body}
                </Box>
                <Signatures tp={tp} />
            </Box>
        </Box>
    );
}

function BandCert({ tp }) {
    const band = { background: tp.accent, px: '11px' };
    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ ...band, py: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Seal color={tp.accent} light />
                <Box sx={{ fontWeight: 800, fontSize: '9px', color: '#fff', lineHeight: 1.15, letterSpacing: '.2px' }}>
                    {tp.title}
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    p: '12px',
                    textAlign: 'center',
                }}
            >
                <Box sx={{ fontFamily: FONTS.serif, fontStyle: 'italic', fontSize: '6px', color: '#a39bb0' }}>
                    {tp.sub}
                </Box>
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#2a2730',
                        lineHeight: 1.1,
                    }}
                >
                    {tp.name}
                </Box>
                <Box sx={{ width: '50%', height: '1px', background: tp.accent, opacity: 0.5 }} />
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontSize: '6px',
                        color: '#a39bb0',
                        lineHeight: 1.4,
                        maxWidth: '94%',
                    }}
                >
                    {tp.body}
                </Box>
                <Signatures tp={tp} />
            </Box>
            <Box sx={{ ...band, py: '4px', textAlign: 'right' }}>
                <Box
                    sx={{
                        fontFamily: FONTS.mono,
                        fontSize: '5px',
                        color: 'rgba(255,255,255,.88)',
                        letterSpacing: '.3px',
                    }}
                >
                    CERTIFICATE ID · {tp.cid}
                </Box>
            </Box>
        </Box>
    );
}

function OrnateCert({ tp }) {
    const corner = { position: 'absolute', width: 8, height: 8, border: `1.5px solid ${tp.accent}`, opacity: 0.5 };
    return (
        <Box sx={{ width: '100%', height: '100%', p: '8px', background: tp.bg }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    border: `1px solid ${tp.accent}`,
                    borderRadius: '2px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    p: '18px 14px',
                    textAlign: 'center',
                }}
            >
                <Box sx={{ ...corner, top: 4, left: 4, borderRight: 'none', borderBottom: 'none' }} />
                <Box sx={{ ...corner, top: 4, right: 4, borderLeft: 'none', borderBottom: 'none' }} />
                <Box sx={{ ...corner, bottom: 4, left: 4, borderRight: 'none', borderTop: 'none' }} />
                <Box sx={{ ...corner, bottom: 4, right: 4, borderLeft: 'none', borderTop: 'none' }} />
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontStyle: 'italic',
                        fontSize: '13px',
                        color: tp.accent,
                        lineHeight: 1,
                    }}
                >
                    Certificate
                </Box>
                <Box sx={{ fontSize: '6px', fontWeight: 600, letterSpacing: '1.5px', color: tp.accent }}>
                    {tp.title}
                </Box>
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontStyle: 'italic',
                        fontSize: '5.5px',
                        color: '#a39bb0',
                        mt: '2px',
                    }}
                >
                    {tp.sub}
                </Box>
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontStyle: 'italic',
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#3a3540',
                        lineHeight: 1.1,
                    }}
                >
                    {tp.name}
                </Box>
                <Box
                    sx={{
                        fontFamily: FONTS.serif,
                        fontSize: '5px',
                        color: '#a39bb0',
                        lineHeight: 1.45,
                        maxWidth: '86%',
                    }}
                >
                    {tp.body}
                </Box>
            </Box>
        </Box>
    );
}

function TemplateCard({ tp }) {
    let inner;
    if (tp.layout === 'img')
        inner = (
            <Box
                component="img"
                src={tp.img}
                alt="Certificate template"
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
        );
    else if (tp.layout === 'classic') inner = <ClassicCert tp={tp} />;
    else if (tp.layout === 'band') inner = <BandCert tp={tp} />;
    else inner = <OrnateCert tp={tp} />;
    return <Box sx={cardSx}>{inner}</Box>;
}

export default function TemplateGallery() {
    const { t } = useTranslation();
    const [cat, setCat] = useState('Graduation');
    const labels = t('home.templates.cats').split(',');
    const cards = TPL[cat] || [];

    return (
        <Box component="section" sx={{ background: '#fff', py: { xs: 6, md: 9 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 4.5 }}>
                    <HeroBadge label={t('home.templates.tag')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.875rem', md: '2.375rem' },
                            fontWeight: 700,
                            lineHeight: 1.15,
                            letterSpacing: '-0.02em',
                            color: INK,
                            mb: 1.5,
                        }}
                    >
                        {t('home.templates.title')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.templates.desc')}
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 1.1,
                        mb: 4.5,
                    }}
                >
                    {CAT_KEYS.map((key, i) => {
                        const on = key === cat;
                        return (
                            <Box
                                key={key}
                                component="button"
                                onClick={() => setCat(key)}
                                sx={{
                                    fontFamily: 'inherit',
                                    fontSize: '0.84rem',
                                    fontWeight: 600,
                                    px: 2.1,
                                    py: 1.1,
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    background: on ? PRIMARY_TINT : '#fff',
                                    color: on ? PURPLE : FG_2,
                                    border: on ? '1px solid transparent' : '1px solid rgba(26,23,38,.12)',
                                }}
                            >
                                {labels[i] || key}
                            </Box>
                        );
                    })}
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                        gap: 2.25,
                        alignItems: 'start',
                    }}
                >
                    {cards.map((tp, i) => (
                        <TemplateCard key={i} tp={tp} />
                    ))}
                </Box>
            </Container>
        </Box>
    );
}
